/**
 * After the story: how we did this (25), every source and the record (26),
 * the close (37).
 */
import { CHAPTERS, chapterName, chapterSpec } from "../deck/deckModel";
import {
  Foot, Headline, Key, Slide, SourceLine, V,
  countWord, dayLong, dayLongYear, dayShort, joinAnd, monthYear, platformName, slideText,
  type TemplateContext,
} from "./kit";

/* ── 25 How we did this ───────────────────────────────────────────────── */

const COUNTS = [
  { key: "accounts_read", label: "Accounts read" },
  { key: "posts_read", label: "Posts read" },
  { key: "comments_read", label: "Comments read" },
  { key: "searches_run", label: "Searches run" },
  { key: "pages_read", label: "Pages read" },
] as const;

export function HowSlide({ ctx }: { ctx: TemplateContext }) {
  const how = ctx.doc.record!.how_we_did_this!;
  const t = slideText(ctx);
  const notes = how.notes ?? {};
  const counts = COUNTS.filter((c) => how[c.key] != null).map((c) => ({ n: how[c.key]!, label: c.label, note: notes[c.key] }));
  const connected = how.connected_platforms;
  const tiles = [
    ...counts,
    ...(connected.length ? [{ n: connected.length, label: connected.length === 1 ? "Platform connected" : "Platforms connected", note: `${joinAnd(connected.map(platformName))}, the brand’s own account${connected.length === 1 ? "" : "s"}` }] : []),
  ];
  const reads = how.reads.map((r) => ({ ...r, spec: chapterSpec(r.chapter) })).filter((r) => r.spec);
  const lastRead = Math.max(0, ...reads.map((r) => r.spec!.number));
  const rows = [
    ...reads.map((r) => ({ n: String(r.spec!.number), title: chapterName(r.spec!).replace(/^Culture and conversation$/, "Culture"), line: r.line })),
    ...(lastRead && lastRead < CHAPTERS.length ? [{ n: `${lastRead + 1} to ${CHAPTERS.length}`, title: "The strategy", line: "judged by the team from the reads above; nothing read anew" }] : []),
  ];
  const half = Math.ceil(rows.length / 2);
  const readAt = how.reads.map((r) => r.read_at).find(Boolean) ?? ctx.doc.record?.sources.map((s) => s.read_at).find(Boolean);
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <EyebrowAfter ctx={ctx} label="How we did this" />
        <Headline text={t.headline} fallback="What Creo read, *counted, not claimed.*" />
        <div className="sv-how">
          <div className="sv-how-counts">
            {tiles.slice(0, 6).map((c, i) => (
              <div key={c.label} className="sv-how-count" data-first={i === 0 || undefined}>
                <V as="b">{c.n.toLocaleString("en-US")}</V>
                <p className="sv-how-key">{c.label}</p>
                {c.note ? <V as="p" className="sv-how-note">{c.note}</V> : null}
              </div>
            ))}
          </div>
          {rows.length ? (
            <div className="sv-how-chapters">
              <Key className="sv-key-ink">What was read for each chapter</Key>
              <div className="sv-how-cols">
                {[rows.slice(0, half), rows.slice(half)].map((col, c) => (
                  <div key={c} className="sv-how-col">
                    {col.map((r) => (
                      <p key={r.n} className="sv-how-row"><b>{r.n}</b><span><strong>{r.title}</strong> · <V>{r.line}</V></span></p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <SourceLine className="sv-source-plain">Counts computed from the read records in Creo{readAt ? <>, <V>{dayLong(readAt)}</V></> : null}; never a claim. The scorecard method: framework 4.3.</SourceLine>
        </div>
        <Foot ctx={ctx} label="How we did this" />
      </div>
    </Slide>
  );
}

/** The eyebrow for the slides after the story, which belong to no chapter. */
function EyebrowAfter({ ctx, label }: { ctx: TemplateContext; label: string }) {
  return (
    <div className="sv-eyebrow">
      <V as="b">{ctx.brand}</V>
      <span>·  {label}</span>
    </div>
  );
}

/* ── 26 Sources and the record ────────────────────────────────────────── */

const host = (url?: string | null) => {
  if (!url) return null;
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return null; }
};

export function SourcesSlide({ ctx }: { ctx: TemplateContext }) {
  const record = ctx.doc.record!;
  const t = slideText(ctx);
  /* Chapters 4 and 5 read the same accounts: one group. */
  const groupOf = (chapter: string) => (chapter === "brand" || chapter === "competitors" ? "accounts" : chapter);
  const groups: Array<{ id: string; title: string; items: typeof record.sources }> = [];
  for (const s of record.sources) {
    const id = groupOf(s.chapter);
    let g = groups.find((x) => x.id === id);
    if (!g) {
      const spec = chapterSpec(s.chapter);
      const title = id === "accounts"
        ? `${chapterSpec("brand")?.number} and ${chapterSpec("competitors")?.number} The accounts`
        : spec ? `${spec.number} ${chapterName(spec).replace(/^Culture and conversation$/, "Culture")}` : s.chapter;
      g = { id, title, items: [] };
      groups.push(g);
    }
    g.items.push(s);
  }
  const order = (id: string) => (id === "accounts" ? 4.5 : chapterSpec(id)?.number ?? 99);
  groups.sort((a, b) => order(a.id) - order(b.id));
  const agency = record.approvals.find((a) => a.role === "agency");
  const client = record.approvals.find((a) => a.role === "client");
  const clientApprover = client ?? ctx.doc.brief?.approvers.find((a) => a.role === "client");
  const who = (a?: { name: string; organisation?: string | null } | null) => (a ? [a.name, a.organisation].filter(Boolean).join(", ") : null);
  const version = ctx.record?.version;
  const versionDate = ctx.record?.updatedAt ?? ctx.record?.liveAt;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <EyebrowAfter ctx={ctx} label="Sources and the record" />
        <Headline text={t.headline} fallback="Every source, *every date, every change.*" />
        <div className="sv-record">
          <div className="sv-record-blocks">
            <div className="sv-record-sources">
              <Key className="sv-key-ink">Sources by chapter</Key>
              {groups.map((g) => (
                <div key={g.id} className="sv-record-group">
                  <p className="sv-record-head">{g.title}</p>
                  {g.items.slice(0, 4).map((s, i) => (
                    <p key={i} className="sv-record-source">
                      <span><V as="b">{s.name}</V>{host(s.url) ? <> · <V>{host(s.url)}</V></> : null}</span>
                      <V className="sv-record-date">{s.date_label ?? dayShort(s.read_at)}</V>
                    </p>
                  ))}
                </div>
              ))}
            </div>
            <div className="sv-record-side">
              <div className="sv-record-card">
                <p className="sv-key sv-key-lime">The record</p>
                {version ? <div><p>Version</p><p><V>{version}</V>{versionDate ? <> · <V>{dayLongYear(versionDate)}</V></> : null}</p></div> : null}
                <div><p>Approved, agency</p><p>{agency ? <><V>{who(agency)}</V>{agency.at ? <> · <V>{dayLong(agency.at)}</V></> : null}</> : "waiting"}</p></div>
                <div><p>Approved, client</p><p>{clientApprover ? <V>{who(clientApprover)}</V> : null}{client?.at ? <> · <V>{dayLong(client.at)}</V></> : " · waiting"}</p></div>
                <div><p>Changed since the last version</p><p>{record.changes.length ? <V>{record.changes.length === 1 ? "One change" : `${countWord(record.changes.length).replace(/^./, (c) => c.toUpperCase())} changes`}: {record.changes.slice(0, 3).map((c) => c.field).join(", ")}</V> : "First version"}</p></div>
              </div>
              {record.open_items.length ? (
                <div className="sv-record-open">
                  <Key className="sv-key-ink">Open items, and what settles each</Key>
                  {record.open_items.slice(0, 5).map((o, i) => (
                    <p key={i}><V as="b">{o.item}</V> · <V>{o.what_would_settle_it}</V></p>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <SourceLine className="sv-source-plain">Sources: every evidence link with its read date, grouped by chapter. The record: approvals and changes from the strategy’s lifecycle; open items are every value still marked open, with what would settle it.</SourceLine>
        </div>
        <Foot ctx={ctx} label="Sources and the record" />
      </div>
    </Slide>
  );
}

/* ── 37 Close ─────────────────────────────────────────────────────────── */

export function CloseSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const t = slideText(ctx);
  const idea = doc.insight?.idea?.line ?? "";
  const pillars = doc.content?.pillars.length ?? 0;
  const total = doc.content?.volume?.monthly_total;
  const goals = doc.goals?.list.length ?? 0;
  const parts = [
    doc.brief?.business_objective ? "the objective" : null,
    idea ? "the idea" : null,
    pillars ? `${countWord(pillars)} pillar${pillars === 1 ? "" : "s"}` : null,
    total ? `${total} posts a month` : null,
    goals ? `${countWord(goals)} number${goals === 1 ? "" : "s"} for the year` : null,
  ].filter((p): p is string => !!p);
  const live = ctx.record?.status === "LIVE";
  const startMonth = ctx.periodStart ? monthYear(ctx.periodStart).split(" ")[0] : null;
  const ask = t.reading
    ? <V>{t.reading}</V>
    : live
      ? <>The strategy is live. The calendar plans from it{startMonth ? <>, from <V>{startMonth}</V></> : null}.</>
      : <>Approve the one page{parts.length ? <>: <V>{joinAnd(parts)}</V></> : null}.{startMonth ? <> Then the calendar starts in <V>{startMonth}</V>.</> : null}</>;
  return (
    <Slide ink className="sv-close">
      <div className="sv-col sv-fill">
        <span className="sv-push" />
        <V as="p" className="sv-close-idea">{t.headline?.replace(/\*/g, "") ?? idea}</V>
        <div className="sv-close-sub">
          <p className="sv-close-key">What this strategy asks</p>
          <p className="sv-close-ask">{ask}</p>
          <p className="sv-close-next">Next · {live ? "The calendar" : "Approval, then the calendar"}</p>
        </div>
        <Foot ctx={ctx} label={null} page={ctx.chapterOf ? `Chapter ${ctx.chapterOf.number} of ${ctx.chapterOf.total}` : ctx.page} />
      </div>
    </Slide>
  );
}
